import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import useAuthStore from "@/store/authStore";
import { MetricTile } from "@/components/dashboards/MetricTile";
import { DeepDiveSheet } from "@/components/dashboards/DeepDiveSheet";
import { PeriodToggle } from "@/components/dashboards/PeriodToggle";
import { BranchUtilisationBars } from "@/components/dashboards/BranchUtilisationBars";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CPC Planner Dashboard — mockup-spec layout.
 *
 *   ┌ header (title · "as of HH:MM") ─────────────────────────────────┐
 *   ┌ REPORTING PERIOD banner (subtitle + period toggle) ─────────────┐
 *   ┌ legend strip (source dots · click hint) ────────────────────────┐
 *   ┌ KEY METRICS (4 large tiles, hairline grid) ─────────────────────┐
 *   ┌ SECONDARY METRICS (3 large tiles) ──────────────────────────────┐
 *   ┌ BRANCH CAPACITY UTILISATION · period (horizontal bars) ─────────┐
 *
 * Period toggle drives one `/api/cpc/dashboard?period=…&include=pit`
 * round-trip. Flow tiles recompute; point-in-time tiles do not (the
 * subtitle calls that out). The branch-utilisation bar chart reads the
 * `branch_utilisation_period` block so it stays in lockstep with the
 * Capacity Utilisation tile (same helper on the backend).
 */
export default function CpcDashboard() {
  const { canRead } = useAuthStore();
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openTile, setOpenTile] = useState(null);
  const [deepDive, setDeepDive] = useState(null);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  const [now, setNow] = useState(new Date());

  const canViewPlan = canRead ? canRead("ProductionPlan") : true;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/cpc/dashboard?period=${period}&include=pit`);
        if (!cancelled) {
          setData(res.data);
          setNow(new Date());
        }
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [period]);

  const flow = data?.flow || {};
  const pit = data?.point_in_time || {};

  // ── period override helpers ─────────────────────────────────────────
  // Per user spec: on the "Today" view, the Total Completed Units and
  // Plan Adherence tiles should display YESTERDAY's numbers (because
  // today's production is in-progress). Week/Month show the live period.
  const useYesterday = period === "today";
  const effCompletedUnits = useYesterday ? flow.completed_units_prev : flow.completed_units;
  const effPlannedUnits   = useYesterday ? flow.planned_units_prev   : flow.planned_units;
  const effCompletedValue = useYesterday ? flow.completed_value_prev : flow.completed_value;
  const effCompletedPrev  = useYesterday ? null : flow.completed_units_prev;
  const effPlannedPrev    = useYesterday ? null : flow.planned_units_prev;
  const effBranches       = useYesterday ? (flow.branch_utilisation_prev || []) : (flow.branch_utilisation_period || []);

  const adherencePct = (effPlannedUnits && effPlannedUnits > 0)
    ? Math.round((effCompletedUnits / effPlannedUnits) * 1000) / 10
    : null;
  const adherencePctPrev = (!useYesterday && effPlannedPrev && effPlannedPrev > 0)
    ? Math.round((effCompletedPrev / effPlannedPrev) * 1000) / 10
    : null;

  const adherenceRag = () => {
    if (adherencePct == null) return "neutral";
    if (adherencePct >= 95) return "green";
    if (adherencePct >= 80) return "amber";
    return "red";
  };
  const adherenceStatus = () => {
    if (adherencePct == null) return "INFO";
    if (adherencePct >= 95) return "HEALTHY";
    if (adherencePct >= 80) return "AT RISK";
    return "BREACH";
  };

  const periodSuffix = period === "today" ? "Yesterday" : period === "week" ? "This Week" : "This Month";

  // ── helpers ─────────────────────────────────────────────────────────
  const fmtN = (v) => (v == null ? "—" : Intl.NumberFormat("en-IN").format(v));
  const fmtINR = (v) => (v == null || v === 0 ? null : `₹${Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(v)}`);
  const fmtPct = (v) => (v == null ? "—" : `${v}%`);
  const deltaPts = (now, prev, unit = "pts") => {
    if (now == null || prev == null) return null;
    const diff = now - prev;
    if (Math.abs(diff) < 0.05) return { sign: 0, label: `0 ${unit}` };
    return {
      sign: diff > 0 ? 1 : -1,
      label: `${diff > 0 ? "+" : ""}${diff.toFixed(1)} ${unit}`,
    };
  };

  // ── status pill resolvers (per mockup: HEALTHY / OK / AT RISK / BREACH / INFO) ─
  const utilStatus = () => {
    const v = flow.utilisation_pct;
    if (v == null) return "INFO";
    if (v >= 70 && v <= 95) return "HEALTHY";
    return "AT RISK";
  };
  const blockingStatus = () => {
    const v = pit?.rm_shortage?.blocking_count;
    if (v == null) return "INFO";
    return v === 0 ? "OK" : "BREACH";
  };
  const overdueStatus = () => {
    const v = pit?.overdue?.count;
    if (v == null) return "INFO";
    if (v === 0) return "OK";
    return v > 5 ? "BREACH" : "AT RISK";
  };
  const unassignedStatus = () => {
    const v = pit?.unassigned?.count;
    if (v == null) return "INFO";
    return v === 0 ? "OK" : "AT RISK";
  };

  // ── RAG colour for tile value ───────────────────────────────────────
  const utilRag = () => {
    const v = flow.utilisation_pct;
    if (v == null) return "neutral";
    if (v >= 70 && v <= 95) return "green";
    if (v < 70) return "amber";
    return "red";
  };
  const blockingRag = () => {
    const v = pit?.rm_shortage?.blocking_count;
    if (v == null) return "neutral";
    return v === 0 ? "green" : "red";
  };
  const overdueRag = () => {
    const v = pit?.overdue?.count;
    if (v == null) return "neutral";
    if (v === 0) return "green";
    return v > 5 ? "red" : "amber";
  };
  const unassignedRag = () => {
    const v = pit?.unassigned?.count;
    if (v == null) return "neutral";
    return v === 0 ? "green" : "amber";
  };

  // ── tile definitions — T1/T2 reworked per user spec ────────────────
  const keyMetrics = useMemo(() => ([
    {
      // T1 — Total Completed Units. Pure count.
      // Today period shows YESTERDAY's count (production in-progress today).
      key: "total_completed",
      label: `Total Completed · ${periodSuffix}`,
      value: fmtN(effCompletedUnits),
      rag: "neutral",
      delta: deltaPts(effCompletedUnits, effCompletedPrev, "units"),
      sparkline: { values: flow?.sparklines?.completed || [], type: "margin" },
      source: "direct",
      subText: fmtINR(effCompletedValue)
        ? `${fmtINR(effCompletedValue)} realised`
        : null,
      footerLeft: useYesterday ? "yesterday's production" : `this ${period}`,
      status: "INFO",
    },
    {
      // T2 — Plan Adherence (NEW calc: completed / planned × 100).
      // Today period shows yesterday's ratio.
      key: "plan_adherence",
      label: `Plan Adherence · ${periodSuffix}`,
      value: fmtPct(adherencePct),
      rag: adherenceRag(),
      delta: deltaPts(adherencePct, adherencePctPrev, "pts"),
      sparkline: { values: flow?.sparklines?.on_time || [], type: "margin" },
      source: "aggregated",
      subText: effPlannedUnits
        ? `${fmtN(effCompletedUnits)} of ${fmtN(effPlannedUnits)} units`
        : "no production scheduled",
      footerLeft: "target 95%",
      status: adherenceStatus(),
    },
    {
      key: "capacity_util",
      label: "Capacity Utilisation",
      value: fmtPct(flow.utilisation_pct),
      rag: utilRag(),
      delta: deltaPts(flow.utilisation_pct, flow.utilisation_pct_prev, "pts"),
      sparkline: { values: flow?.sparklines?.utilisation || [], type: "sp" },
      source: "aggregated",
      footerLeft: "70-95% band",
      status: utilStatus(),
    },
    {
      key: "rm_blocking",
      label: "RM Shortages Blocking",
      value: fmtN(pit?.rm_shortage?.blocking_count),
      rag: blockingRag(),
      delta: null,
      sparkline: pit?.rm_shortage?.sparkline?.length >= 2
        ? { values: pit.rm_shortage.sparkline, type: "cost" }
        : null,
      source: "aggregated",
      subText: pit?.rm_shortage?.short_count != null
        ? `point-in-time · ${fmtN(pit.rm_shortage.short_count)} short total`
        : null,
      footerLeft: "target 0",
      status: blockingStatus(),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [flow, pit, period, effCompletedUnits, effPlannedUnits, effCompletedValue, adherencePct]);

  const secondaryMetrics = useMemo(() => ([
    {
      key: "overdue",
      label: "Overdue Schedules",
      value: fmtN(pit?.overdue?.count),
      rag: overdueRag(),
      delta: null,
      sparkline: pit?.overdue?.sparkline_new_overdue?.length
        ? { values: pit.overdue.sparkline_new_overdue, type: "cost" }
        : null,
      source: "direct",
      subText: pit?.overdue?.units_stuck != null
        ? `UNITS STUCK · ${fmtN(pit.overdue.units_stuck)}`
        : null,
      footerLeft: "past target date",
      status: overdueStatus(),
    },
    {
      key: "unassigned",
      label: "Unassigned Schedules",
      value: fmtN(pit?.unassigned?.count),
      rag: unassignedRag(),
      delta: null,
      sparkline: null,
      source: "direct",
      subText: pit?.unassigned?.count === 0 ? "all branched" : null,
      footerLeft: "must be 0",
      status: unassignedStatus(),
    },
    {
      key: "available_7d",
      label: "Available Capacity (7D)",
      value: fmtN(pit?.available_capacity_7d?.available_units),
      rag: "neutral",
      delta: null,
      sparkline: null,
      source: "aggregated",
      subText: pit?.available_capacity_7d?.available_pct != null
        ? `${pit.available_capacity_7d.available_pct}% headroom`
        : null,
      footerLeft: "next 7 days",
      status: "INFO",
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [pit]);

  // ── deep-dive loaders (kept identical to prior pass) ────────────────
  const loadDeepDive = async (key) => {
    setDeepDiveLoading(true);
    setDeepDive(null);
    try {
      const ws = data?.window?.start;
      const we = data?.window?.end;
      if (key === "total_completed") {
        // Branch-wise total completed units for the effective window
        // (yesterday on Today period, current on Week/Month).
        const rows = (effBranches || [])
          .filter((b) => (b.allocated || 0) > 0 || (b.completed || 0) > 0)
          .map((b) => ({
            branch: b.branch,
            allocated: b.allocated,
            completed: b.completed,
            adherence_pct: b.adherence_pct,
          }))
          .sort((a, b) => (b.completed || 0) - (a.completed || 0));
        setDeepDive({ kind: "branch_completed", rows });
      } else if (key === "plan_adherence") {
        // Branch-wise planned / completed / adherence% for the effective window.
        const rows = (effBranches || [])
          .filter((b) => (b.allocated || 0) > 0)
          .map((b) => ({
            branch: b.branch,
            allocated: b.allocated,
            completed: b.completed,
            adherence_pct: b.adherence_pct,
          }))
          // Worst adherence first — that's what the planner needs to act on
          .sort((a, b) => (a.adherence_pct ?? 999) - (b.adherence_pct ?? 999));
        setDeepDive({ kind: "branch_adherence", rows });
      } else if (key === "open_vs_completed") {
        const res = await axios.get(`${API}/production-schedules`);
        const all = Array.isArray(res.data) ? res.data : [];
        const inW = ws && we ? all.filter((r) => r.target_date && r.target_date >= ws && r.target_date < we) : all;
        setDeepDive({ kind: "schedules", rows: inW.slice(0, 50) });
      } else if (key === "plan_adherence") {
        const res = await axios.get(`${API}/production-schedules`);
        const all = Array.isArray(res.data) ? res.data : [];
        const late = all
          .filter((r) => r.status === "COMPLETED" && r.completed_at && r.target_date && r.completed_at > r.target_date)
          .map((r) => {
            const ca = new Date(r.completed_at);
            const td = new Date(r.target_date);
            return { ...r, days_late: Math.max(0, Math.round((ca - td) / 86400000)) };
          })
          .sort((a, b) => b.days_late - a.days_late)
          .slice(0, 50);
        setDeepDive({ kind: "late", rows: late });
      } else if (key === "capacity_util") {
        const rows = (flow.branch_utilisation_period || []).map((b) => ({
          branch: b.branch,
          capacity: b.capacity,
          allocated: b.allocated,
          util_pct: b.util_pct,
          no_capacity: !b.capacity,
        })).sort((a, b) => (b.util_pct || 0) - (a.util_pct || 0));
        setDeepDive({ kind: "utilisation", rows });
      } else if (key === "overdue") {
        const res = await axios.get(`${API}/production-schedules`);
        const all = Array.isArray(res.data) ? res.data : [];
        const nowIso = new Date().toISOString();
        const rows = all
          .filter((r) => r.target_date && r.target_date < nowIso && !["COMPLETED", "CANCELLED", "DELETED"].includes(r.status))
          .map((r) => ({ ...r, days_overdue: Math.max(0, Math.round((Date.now() - new Date(r.target_date).getTime()) / 86400000)) }))
          .sort((a, b) => b.days_overdue - a.days_overdue)
          .slice(0, 50);
        setDeepDive({ kind: "overdue", rows });
      } else if (key === "unassigned") {
        const res = await axios.get(`${API}/cpc/unassigned-schedules`);
        setDeepDive({ kind: "unassigned", rows: res.data?.schedules || [] });
      } else if (key === "available_7d") {
        const res = await axios.get(`${API}/cpc/available-capacity`);
        const all = res.data?.data || [];
        const byBranch = {};
        all.forEach((r) => {
          if (!byBranch[r.branch]) byBranch[r.branch] = { branch: r.branch, capacity_7d: 0, allocated_7d: 0, available: 0, days: 0 };
          if (byBranch[r.branch].days < 7) {
            byBranch[r.branch].capacity_7d += r.capacity;
            byBranch[r.branch].allocated_7d += r.scheduled;
            byBranch[r.branch].available += r.available;
            byBranch[r.branch].days += 1;
          }
        });
        const rows = Object.values(byBranch).map((b) => ({
          ...b,
          available_pct: b.capacity_7d ? Math.round((b.available / b.capacity_7d) * 1000) / 10 : 0,
        })).sort((a, b) => a.available_pct - b.available_pct);
        setDeepDive({ kind: "available_7d", rows });
      } else if (key === "rm_blocking") {
        const res = await axios.get(`${API}/cpc/rm-shortage-report`);
        const all = [];
        (res.data?.branches || []).forEach((b) => {
          (b.shortages || []).filter((s) => s.shortage > 0).forEach((s) => {
            all.push({
              branch: b.branch,
              rm_id: s.rm_id,
              category: s.category,
              current_stock: s.available,
              required: s.required,
              deficit: s.shortage,
              status: s.status,
              description: s.description,
            });
          });
        });
        all.sort((a, b) => b.deficit - a.deficit);
        setDeepDive({ kind: "shortage", rows: all.slice(0, 50) });
      }
    } catch (e) {
      setDeepDive({ kind: "error", rows: [], error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setDeepDiveLoading(false);
    }
  };

  const openDeepDive = (key, label) => {
    setOpenTile({ key, label });
    loadDeepDive(key);
  };

  const periodLabel = period === "today" ? "today" : period === "week" ? "this week" : "this month";
  const asOfText = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (!canViewPlan) {
    return (
      <div className="p-8" data-testid="cpc-dashboard-no-access">
        <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">
          You do not have access to the CPC dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-[1600px] mx-auto" data-testid="cpc-dashboard-page">
      {/* ── Title row ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-5xl font-black tracking-tight uppercase">CPC Planner</h1>
          <p className="mt-2 text-xs font-mono text-muted-foreground tracking-wide">
            Production planning &amp; scheduling · universal (all branches)
          </p>
        </div>
        <div className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">
          as of {asOfText}
        </div>
      </div>

      {/* ── Reporting Period banner ──────────────────────────────── */}
      <div className="border border-border bg-white px-6 py-4 mb-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
            Reporting Period
          </div>
          <p className="mt-1 text-xs font-mono text-zinc-700">
            Flow tiles recompute · Today also shows yesterday&apos;s achievement · state tiles are point-in-time
          </p>
        </div>
        <PeriodToggle period={period} onChange={setPeriod} />
      </div>

      {/* ── Legend strip ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-8 text-[11px] font-mono tracking-wide text-muted-foreground">
        <div className="flex items-center gap-6">
          <span className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-green-500" /> Direct endpoint
          </span>
          <span className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber-500" /> Needs aggregation
          </span>
        </div>
        <div>
          Click any tile <span className="mx-1">→</span> deep-dive
          {loading && <span className="ml-3 text-amber-700 uppercase">loading…</span>}
        </div>
      </div>

      {/* ── KEY METRICS ──────────────────────────────────────────── */}
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">
        Key Metrics
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-10">
        {keyMetrics.map((t) => (
          <MetricTile
            key={t.key}
            label={t.label}
            value={t.value}
            rag={t.rag}
            delta={t.delta}
            sparkline={t.sparkline}
            source={t.source}
            subText={t.subText}
            footerLeft={t.footerLeft}
            status={t.status}
            onClick={() => openDeepDive(t.key, t.label)}
            testId={`cpc-tile-${t.key}`}
          />
        ))}
      </div>

      {/* ── SECONDARY METRICS ────────────────────────────────────── */}
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">
        Secondary Metrics
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border mb-10">
        {secondaryMetrics.map((t) => (
          <MetricTile
            key={t.key}
            label={t.label}
            value={t.value}
            rag={t.rag}
            delta={t.delta}
            sparkline={t.sparkline}
            source={t.source}
            subText={t.subText}
            footerLeft={t.footerLeft}
            status={t.status}
            onClick={() => openDeepDive(t.key, t.label)}
            testId={`cpc-tile-${t.key}`}
          />
        ))}
      </div>

      {/* ── Branch utilisation bar chart ─────────────────────────── */}
      <BranchUtilisationBars
        rows={flow.branch_utilisation_period}
        periodLabel={periodLabel}
      />

      <DeepDiveSheet
        open={!!openTile}
        onOpenChange={(v) => { if (!v) { setOpenTile(null); setDeepDive(null); } }}
        title={openTile?.label || ""}
        description={deepDiveLoading ? "loading…" : `${deepDive?.rows?.length ?? 0} row(s) · ${period}`}
      >
        <DeepDiveBody deepDive={deepDive} loading={deepDiveLoading} />
      </DeepDiveSheet>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Deep-dive body — one table per tile kind (identical to prior pass)
// ─────────────────────────────────────────────────────────────────────────────
function DeepDiveBody({ deepDive, loading }) {
  if (loading) return <div className="p-6 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground">loading rows…</div>;
  if (!deepDive) return null;
  if (deepDive.kind === "error") return <div className="p-4 text-xs text-red-600 font-mono">{deepDive.error}</div>;
  const rows = deepDive.rows || [];
  if (rows.length === 0) {
    return <div className="p-6 text-center text-xs font-mono italic text-muted-foreground">No rows for this tile in the current window.</div>;
  }
  const ch = "p-2 text-left font-mono uppercase tracking-wider text-[10px] text-muted-foreground bg-zinc-50";
  const chR = `${ch} text-right`;
  const c = "p-2 text-xs font-mono border-t border-border";

  if (deepDive.kind === "branch_completed") {
    const total = rows.reduce((acc, r) => acc + (r.completed || 0), 0);
    return (
      <div data-testid="deep-dive-branch-completed">
        <div className="px-2 pb-3 text-xs font-mono text-muted-foreground">
          Branch-wise completion · <span className="text-zinc-900 font-bold">{Intl.NumberFormat("en-IN").format(total)}</span> total units
        </div>
        <table className="w-full">
          <thead><tr><th className={ch}>Branch</th><th className={chR}>Planned</th><th className={chR}>Completed</th><th className={chR}>Adherence</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.branch}>
              <td className={c}>{r.branch}</td>
              <td className={`${c} text-right`}>{Intl.NumberFormat("en-IN").format(r.allocated || 0)}</td>
              <td className={`${c} text-right font-bold`}>{Intl.NumberFormat("en-IN").format(r.completed || 0)}</td>
              <td className={`${c} text-right ` + (r.adherence_pct == null ? "text-muted-foreground" : r.adherence_pct >= 95 ? "text-green-700" : r.adherence_pct >= 80 ? "text-amber-700" : "text-red-600")}>
                {r.adherence_pct == null ? "—" : `${r.adherence_pct}%`}
              </td>
            </tr>))}</tbody>
        </table>
      </div>
    );
  }
  if (deepDive.kind === "branch_adherence") {
    const totPlanned = rows.reduce((a, r) => a + (r.allocated || 0), 0);
    const totDone = rows.reduce((a, r) => a + (r.completed || 0), 0);
    const overall = totPlanned ? Math.round((totDone / totPlanned) * 1000) / 10 : null;
    return (
      <div data-testid="deep-dive-branch-adherence">
        <div className="px-2 pb-3 text-xs font-mono text-muted-foreground">
          Branch-wise plan adherence · overall{" "}
          <span className={"font-bold " + (overall == null ? "text-zinc-900" : overall >= 95 ? "text-green-700" : overall >= 80 ? "text-amber-700" : "text-red-600")}>
            {overall == null ? "—" : `${overall}%`}
          </span>
        </div>
        <table className="w-full">
          <thead><tr><th className={ch}>Branch</th><th className={chR}>Planned</th><th className={chR}>Completed</th><th className={chR}>Adherence</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.branch}>
              <td className={c}>{r.branch}</td>
              <td className={`${c} text-right`}>{Intl.NumberFormat("en-IN").format(r.allocated || 0)}</td>
              <td className={`${c} text-right`}>{Intl.NumberFormat("en-IN").format(r.completed || 0)}</td>
              <td className={`${c} text-right font-bold ` + (r.adherence_pct == null ? "text-muted-foreground" : r.adherence_pct >= 95 ? "text-green-700" : r.adherence_pct >= 80 ? "text-amber-700" : "text-red-600")}>
                {r.adherence_pct == null ? "—" : `${r.adherence_pct}%`}
              </td>
            </tr>))}</tbody>
        </table>
      </div>
    );
  }
  if (deepDive.kind === "schedules") {
    return (
      <table className="w-full" data-testid="deep-dive-schedules">
        <thead><tr><th className={ch}>Schedule</th><th className={ch}>Branch · SKU</th><th className={chR}>Target</th><th className={chR}>Done</th><th className={ch}>Status</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.schedule_id || r.id || i}>
            <td className={c}>{r.schedule_code || r.schedule_id || r.id}</td>
            <td className={c}>{r.branch} · {r.sku_id}</td>
            <td className={`${c} text-right`}>{r.target_quantity}</td>
            <td className={`${c} text-right`}>{r.completed_quantity || 0}</td>
            <td className={c}><span className={"text-[10px] uppercase tracking-wider px-2 py-0.5 border " + (r.status === "COMPLETED" ? "border-green-600 text-green-700" : "border-zinc-400 text-zinc-600")}>{r.status}</span></td>
          </tr>))}</tbody>
      </table>
    );
  }
  if (deepDive.kind === "late") {
    return (
      <table className="w-full" data-testid="deep-dive-late">
        <thead><tr><th className={ch}>Schedule</th><th className={ch}>Branch · SKU</th><th className={ch}>Target</th><th className={ch}>Done At</th><th className={chR}>Days Late</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.schedule_id || r.id || i}>
            <td className={c}>{r.schedule_code || r.schedule_id}</td>
            <td className={c}>{r.branch} · {r.sku_id}</td>
            <td className={c}>{(r.target_date || "").slice(0, 10)}</td>
            <td className={c}>{(r.completed_at || "").slice(0, 10)}</td>
            <td className={`${c} text-right text-red-600 font-bold`}>{r.days_late}</td>
          </tr>))}</tbody>
      </table>
    );
  }
  if (deepDive.kind === "utilisation") {
    return (
      <div className="space-y-2" data-testid="deep-dive-utilisation">
        {rows.map((b) => (
          <div key={b.branch} className="border border-border bg-white p-3 flex items-center justify-between">
            <div>
              <div className="font-mono text-sm font-bold">{b.branch}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{b.allocated} / {b.capacity} units</div>
              {b.no_capacity && <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-amber-700">⚠ no capacity set</div>}
            </div>
            <div className={"text-2xl font-black font-mono " + (b.util_pct > 100 ? "text-red-600" : b.util_pct >= 70 ? "text-green-700" : b.util_pct >= 40 ? "text-zinc-700" : "text-amber-700")}>{b.util_pct}%</div>
          </div>))}
      </div>
    );
  }
  if (deepDive.kind === "overdue") {
    return (
      <table className="w-full" data-testid="deep-dive-overdue">
        <thead><tr><th className={ch}>Schedule</th><th className={ch}>Branch · SKU</th><th className={ch}>Target</th><th className={chR}>Days Over</th><th className={chR}>Done</th><th className={chR}>Target Qty</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.schedule_id || r.id || i}>
            <td className={c}>{r.schedule_code || r.schedule_id}</td>
            <td className={c}>{r.branch} · {r.sku_id}</td>
            <td className={c}>{(r.target_date || "").slice(0, 10)}</td>
            <td className={`${c} text-right text-red-600 font-bold`}>{r.days_overdue}</td>
            <td className={`${c} text-right`}>{r.completed_quantity || 0}</td>
            <td className={`${c} text-right`}>{r.target_quantity}</td>
          </tr>))}</tbody>
      </table>
    );
  }
  if (deepDive.kind === "unassigned") {
    return (
      <table className="w-full" data-testid="deep-dive-unassigned">
        <thead><tr><th className={ch}>Schedule</th><th className={ch}>SKU</th><th className={chR}>Target Qty</th><th className={ch}>Target</th><th className={ch}>Branch (raw)</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.schedule_id || r.id || i}>
            <td className={c}>{r.schedule_code || r.schedule_id || r.id}</td>
            <td className={c}>{r.sku_id}</td>
            <td className={`${c} text-right`}>{r.target_quantity}</td>
            <td className={c}>{(r.target_date || "").slice(0, 10)}</td>
            <td className={c + " text-zinc-400"}>{r.branch === null ? "null" : r.branch === "" ? "(empty)" : r.branch || "—"}</td>
          </tr>))}</tbody>
      </table>
    );
  }
  if (deepDive.kind === "available_7d") {
    return (
      <table className="w-full" data-testid="deep-dive-available-7d">
        <thead><tr><th className={ch}>Branch</th><th className={chR}>Capacity 7d</th><th className={chR}>Allocated</th><th className={chR}>Available</th><th className={chR}>Avail %</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.branch}>
            <td className={c}>{r.branch}</td>
            <td className={`${c} text-right`}>{Intl.NumberFormat("en-IN").format(r.capacity_7d)}</td>
            <td className={`${c} text-right`}>{Intl.NumberFormat("en-IN").format(r.allocated_7d)}</td>
            <td className={`${c} text-right`}>{Intl.NumberFormat("en-IN").format(r.available)}</td>
            <td className={`${c} text-right font-bold ` + (r.available_pct < 10 ? "text-red-600" : r.available_pct > 75 ? "text-amber-700" : "text-green-700")}>{r.available_pct}%</td>
          </tr>))}</tbody>
      </table>
    );
  }
  if (deepDive.kind === "shortage") {
    return (
      <table className="w-full" data-testid="deep-dive-shortage">
        <thead><tr><th className={ch}>Branch · RM</th><th className={ch}>Description</th><th className={chR}>Required</th><th className={chR}>Stock</th><th className={chR}>Deficit</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={`${r.branch}-${r.rm_id}-${i}`}>
            <td className={c + " text-blue-700"}>{r.branch} · {r.rm_id}</td>
            <td className={c + " text-zinc-600 max-w-[200px] truncate"}>{r.description || r.category}</td>
            <td className={`${c} text-right`}>{Intl.NumberFormat("en-IN").format(r.required)}</td>
            <td className={`${c} text-right ` + (r.current_stock === 0 ? "text-red-600 font-bold" : "")}>{Intl.NumberFormat("en-IN").format(r.current_stock)}</td>
            <td className={`${c} text-right text-red-600 font-bold`}>{Intl.NumberFormat("en-IN").format(r.deficit)}</td>
          </tr>))}</tbody>
      </table>
    );
  }
  return null;
}
